import { Button } from '@neo/index'
import { cn } from '@neo/lib/utils'
import { Check, Headphones, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useMonitorMutations } from 'v2/pages/conversations/mutations/call'
import { usePhoneStore } from 'v2/state/phone'
import { MonitorStates } from 'v2/types/call'
import { PubnubEvents } from 'v2/types/pubnub'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'

export const MonitoringBannerAndControls = () => {
  const { parentConversation } = usePhoneStore((state) => state.callConversation)
  const monitoringState = usePhoneStore((state) => state.monitoringState)
  const isSecondaryParticipant = usePhoneStore((state) => state.isSecondaryParticipant)
  const isMonitorInitiator = usePhoneStore((state) => state.isMonitorInitiator)
  const { notifyMonitoring, startCoaching, rejectAssistance, requestHelp } = useMonitorMutations()

  const isBarging = monitoringState === MonitorStates.BARGE
  const isCoaching = monitoringState === MonitorStates.COACH
  const isAssistanceRequested = monitoringState === MonitorStates.ASSISTANCE_REQUESTED // This is for supervisor
  const [isAssistanceRequestedAgent, setIsAssistanceRequestedAgent] = useState<
    null | 'requested' | 'rejected'
  >(null) // This is for agent

  useEffect(() => {
    const unbinds = [
      pubnubEventBus.on(PubnubEvents.ASSISTANCE_REJECTED, () => {
        setIsAssistanceRequestedAgent('rejected')
        const timeout = setTimeout(() => setIsAssistanceRequestedAgent(null), 3000)
        return () => clearTimeout(timeout)
      }),
    ]
    return () => unbinds.forEach((unbind) => unbind())
  }, [])

  if (!parentConversation?.conversation_number) return null
  if (isBarging) return null

  const bannerText = () => {
    if (monitoringState === MonitorStates.MONITOR) return 'Monitoring in incognito'
    if (monitoringState === MonitorStates.MONITOR_VISIBLE || isAssistanceRequested)
      if (!isSecondaryParticipant) {
        if (isAssistanceRequestedAgent === 'requested') return 'Waiting for supervisor to respond'
        if (isAssistanceRequestedAgent === 'rejected') return 'Supervisor rejected your request'
        return 'Supervisor Monitoring'
      } else return 'Monitoring in progress'
    if (monitoringState === MonitorStates.COACH) return 'Coaching in progress'
    return ''
  }

  return (
    <div className="-mx-4 -mb-3 mt-2">
      <div
        className={cn(
          'flex items-center gap-2 bg-status-success/10 py-1 px-4 justify-between h-[26px]',
          isCoaching && 'bg-status-warning/10',
        )}
      >
        <span className="text-xs-400 text-secondary">{bannerText()}</span>
        {isMonitorInitiator && monitoringState === MonitorStates.MONITOR && (
          <button
            className={cn(
              'font-jetbrains text-label uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
              notifyMonitoring.isPending && 'opacity-50',
            )}
            type="button"
            disabled={notifyMonitoring.isPending}
            onClick={() => notifyMonitoring.mutate(parentConversation?.conversation_number)}
          >
            Notify Agent
          </button>
        )}
        {!isSecondaryParticipant &&
          monitoringState === MonitorStates.MONITOR_VISIBLE &&
          !isAssistanceRequestedAgent && (
            <button
              className={cn(
                'font-jetbrains text-label uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded',
                requestHelp.isPending && 'opacity-50',
              )}
              type="button"
              onClick={() =>
                requestHelp.mutate(parentConversation?.conversation_number, {
                  onSuccess: () => setIsAssistanceRequestedAgent('requested'),
                })
              }
              disabled={requestHelp.isPending}
            >
              Request help
            </button>
          )}
      </div>
      <div>
        {isAssistanceRequested && (
          <div className="flex gap-2 bg-error/10 p-4 justify-between">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-error">
                  <Headphones className="h-4 w-4" />
                </span>
                <span className="text-xs-400 text-secondary">Assistance required</span>
              </div>
              <p className="text-xs-400 text-tertiary italic">Agent has requested for assistance</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive-outline"
                className="rounded-full"
                size="iconSm"
                data-testid="button-reject-assistance"
                tooltipContent="Reject assistance request"
                onClick={() => {
                  rejectAssistance.mutate(parentConversation?.conversation_number)
                }}
              >
                <X className="h-4 w-4 text-destructive" />
              </Button>
              <Button
                variant="success-outline"
                className="rounded-full"
                size="iconSm"
                data-testid="button-accept-assistance"
                tooltipContent="Accept assistance request"
                onClick={() => {
                  startCoaching.mutate({
                    conversation_id: parentConversation?.conversation_number,
                    agent_requested_coach: true,
                  })
                }}
              >
                <Check className="h-4 w-4 text-status-success" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
